import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { authTheme, ink, brass, paper, ruledLinesBg } from "./auth-theme";

/**
 * Chrome comum das páginas de autenticação: painel de marca (esquerda, com
 * a textura de pauta de caderno) + painel do formulário (direita).
 * Cada página só precisa fornecer o conteúdo do formulário via `children`.
 */
export default function AuthLayout({
  eyebrow = "Portal Acadêmico",
  headline,
  logoUrl,
  storeLinks = [],
  children,
}) {
  return (
    <ThemeProvider theme={authTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          backgroundColor: paper,
        }}
      >
        {/* Painel de marca */}
        <Box
          sx={{
            flex: { xs: "0 0 auto", md: "0 0 45%" },
            minHeight: { xs: 220, md: "100vh" },
            background: `linear-gradient(160deg, ${ink[900]} 0%, ${ink[800]} 60%, ${ink[700]} 100%)`,
            ...ruledLinesBg,
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            px: { xs: 4, md: 7 },
            py: { xs: 4, md: 7 },
          }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: brass,
                letterSpacing: "0.18em",
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              {eyebrow}
            </Typography>
            {logoUrl && (
              <Box sx={{ maxWidth: 200, my: 3 }}>
                <img
                  src={logoUrl}
                  alt="Logo"
                  style={{ width: "100%", display: "block" }}
                />
              </Box>
            )}
            {headline && (
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: 28, md: 34 },
                  color: "black",
                  fontWeight: 600,
                  lineHeight: 1.25,
                  maxWidth: 360,
                  display: { xs: "none", md: "block" },
                }}
              >
                {headline}
              </Typography>
            )}
          </Box>

          {storeLinks.length > 0 && (
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                gap: 2,
                opacity: 0.85,
              }}
            >
              {storeLinks.map((store) => (
                <a
                  key={store.href}
                  href={store.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img style={{ width: 150 }} src={store.src} alt={store.alt} />
                </a>
              ))}
            </Box>
          )}
        </Box>

        {/* Painel do formulário */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 3, sm: 6 },
            py: { xs: 6, md: 4 },
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 360 }}>
            {children}

            {storeLinks.length > 0 && (
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  gap: 2,
                  justifyContent: "center",
                  mt: 5,
                }}
              >
                {storeLinks.map((store) => (
                  <a
                    key={store.href}
                    href={store.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      style={{ width: 130 }}
                      src={store.src}
                      alt={store.alt}
                    />
                  </a>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

/** Título + rule dourado, reaproveitado no topo de cada formulário. */
export function AuthFormHeading({ title, subtitle }) {
  return (
    <>
      <Typography
        variant="h1"
        sx={{ fontSize: 26, fontWeight: 600, color: ink[900] }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          width: 44,
          height: 3,
          backgroundColor: brass,
          my: 2,
          borderRadius: 2,
        }}
      />
      {subtitle && (
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
          {subtitle}
        </Typography>
      )}
    </>
  );
}
